import { CalendarClock, Infinity as InfinityIcon, TimerOff } from 'lucide-react';
import { format } from 'date-fns';
import { membershipExpiryState } from '../../../utils/staffTenure';
import { cn } from '../../../utils/cn';

interface MembershipExpiryBadgeProps {
  expiresAt: number | null;
  /** Render a subtle "Permanent" pill when there is no expiry (default: render nothing). */
  showPermanent?: boolean;
  className?: string;
}

/** Expiry pill for a team membership: permanent · expires in N days · expiring soon (amber) · expired (red). */
export const MembershipExpiryBadge = ({ expiresAt, showPermanent = false, className }: MembershipExpiryBadgeProps) => {
  const info = membershipExpiryState(expiresAt);
  const base = 'inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-semibold border whitespace-nowrap';

  if (info.state === 'none') {
    if (!showPermanent) return null;
    return (
      <span className={cn(base, 'bg-glass-100/5 text-glass-400 border-glass-200/20', className)} title="No expiry">
        <InfinityIcon className="w-3 h-3" aria-hidden="true" />
        Permanent
      </span>
    );
  }

  const when = format(new Date(expiresAt as number), 'd MMM yyyy HH:mm');
  if (info.state === 'expired') {
    return (
      <span className={cn(base, 'bg-red-500/15 text-red-300 border-red-500/30', className)} title={`Expired ${when}`}>
        <TimerOff className="w-3 h-3" aria-hidden="true" />
        Expired {Math.abs(info.days ?? 0)}d ago
      </span>
    );
  }
  const days = info.days ?? 0;
  const label = days <= 1 ? 'Expires today' : `Expires in ${days}d`;
  return (
    <span
      className={cn(
        base,
        info.state === 'expiring' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' : 'bg-blue-500/10 text-blue-300 border-blue-500/25',
        className,
      )}
      title={`Expires ${when}`}
    >
      <CalendarClock className="w-3 h-3" aria-hidden="true" />
      {label}
    </span>
  );
};
